import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { actionAuth } from "../slices/AuthSlice";
import { getToken, saveTokenInLocalStorage } from "../../Services/AuthService";
import Swal from "sweetalert2";

export const AuthApi = createApi({
  reducerPath: "AuthApi",
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_API_HOST,
    prepareHeaders: (headers, { getState }) => {
      const token = getState().auth.token;

      // If we have a token set in state, let's assume that we should be passing it.
      if (token) {
        headers.set("authorization", `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ["Profile"],
  endpoints: (builder) => ({
    doLogin: builder.mutation({
      query: (data) => ({
        url: "candidate/login",
        method: "POST",
        body: data,
      }),
    }),
    verifyOTP: builder.mutation({
      query: (data) => ({
        url: "candidate/otp_verification",
        method: "POST",
        body: data,
      }),
      onQueryStarted: async (arg, api) => {
        const { dispatch, queryFulfilled } = api;
        try {
          const { data } = await queryFulfilled;
          if (data.data?.isGuest === undefined) {
            const savedata = {
              data: data.data,
              is_job_fair_live: data.is_job_fair_live,
              job_fair_banner: data.job_fair_banner,
              job_fair_id: data.job_fair_id,
              token: data.token,
            };
            saveTokenInLocalStorage(savedata);
            dispatch(actionAuth(savedata));
          }
        } catch (error) {
          Swal.fire("Error", error?.error?.data?.error, "error");
        }
      },
    }),
    registerUser: builder.mutation({
      query: (data) => ({
        url: "candidate/registration",
        method: "POST",
        body: data,
      }),
      onQueryStarted: async (arg, api) => {
        const { dispatch, queryFulfilled } = api;
        try {
          const { data } = await queryFulfilled;
          if (data.data?.isGuest === undefined) {
            const savedata = {
              data: data.data,
              is_job_fair_live: data.is_job_fair_live,
              job_fair_banner: data.job_fair_banner,
              job_fair_id: data.job_fair_id,
              token: data.token,
            };
            saveTokenInLocalStorage(savedata);
            dispatch(actionAuth(savedata));
          }
        } catch (error) {
          Swal.fire("Error", error?.error?.data?.error, "error");
        }
      },
    }),
    userProfile: builder.query({
      query: (data) => ({
        url: "candidate/profile",
        method: "POST",
      }),
      providesTags: ["Profile"],
      onQueryStarted: async (arg, api) => {
        const { dispatch, queryFulfilled } = api;
        try {
          const { data } = await queryFulfilled;
          if (data.data?.isGuest === undefined) {
            const token = await getToken();
            const savedata = {
              data: data.data,
              is_job_fair_live: data.is_job_fair_live,
              job_fair_banner: data.job_fair_banner,
              job_fair_id: data.job_fair_id,
              token: token,
            };
            saveTokenInLocalStorage(savedata);
            dispatch(actionAuth(savedata));
          }
        } catch (error) {
          Swal.fire("Error", error?.error?.data?.error, "error");
        }
      },
    }),
    updateProfile: builder.mutation({
      query: (data) => ({
        url: "candidate/updateprofile",
        method: "PUT",
        body: data,
      }),
      onQueryStarted: async (arg, api) => {
        const { dispatch, queryFulfilled } = api;
        try {
          const { data } = await queryFulfilled;
          if (data.data?.isGuest === undefined) {
            const token = await getToken();
            const savedata = { data: data.data, token: token };
            saveTokenInLocalStorage(savedata);
            dispatch(actionAuth(savedata));
          }
        } catch (error) {
          Swal.fire("Error", error?.error?.data?.error, "error");
        }
      },
    }),
    uploadImage: builder.mutation({
      query: (file) => {
        const body = new FormData();
        body.append("Content-Type", file.type);
        body.append("profile_picture", file);
        return {
          url: "candidate/upload/update-profile-picture",
          method: "PUT",
          body: body,
        };
      },
    }),
    addJobFair: builder.mutation({
      query: (data) => ({
        url: "candidate/add-job-fair",
        method: "POST",
        body: data,
      }),
      onQueryStarted: async (arg, api) => {
        const { dispatch, queryFulfilled } = api;
        try {
          const { data } = await queryFulfilled;
          //console.log(data);
        } catch (error) {
          Swal.fire("Error", error?.error?.data?.error, "error");
        }
      },
    }),
    logOut: builder.mutation({
      query: (data) => ({
        url: "admin/user/logout",
        method: "POST",
        body: data,
      }),
    }),
  }),
});

export const {
  useDoLoginMutation,
  useVerifyOTPMutation,
  useRegisterUserMutation,
  useUserProfileQuery,
  useUpdateProfileMutation,
  useUploadImageMutation,
  useAddJobFairMutation,
  useLogOutMutation
} = AuthApi;
